import { AccountTypes } from '@interfaces/models/account-types';
import { OAAccountTypes } from '@interfaces/open-account/enums';
import { IOpenAccount } from '@interfaces/open-account/oa.interface';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { FlowTypes, IRAOptions, IRATypes } from '@shared/models';
import { VgEmploymentState, VgPersonalAddressState } from 'vg-interaction-model';
import { IFinancialsState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/financials.reducer';
import { IInvestmentObjectivesState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/investment-objectives.reducer';

import { ITransferTypes, PersonalStates } from './oa.selectors';

export default function clientDataOAMapper(
  cooPersonalStates: PersonalStates,
  cooContactInfo: VgPersonalAddressState,
  employment: VgEmploymentState,
  financial: IFinancialsState,
  invObj: IInvestmentObjectivesState,
  transactionTypes: ITransferTypes,
  settlementFund: SettlementFundAoAEnum | null,
  isProspect: boolean = false,
): IOpenAccount {
  const oaPersonalInfo = [
    {
      firstName: cooPersonalStates.personalInfo.firstName,
      middleName: cooPersonalStates.personalInfo.middleName,
      suffix: cooPersonalStates.personalInfo.suffix,
      lastName: cooPersonalStates.personalInfo.lastName,
      email: cooPersonalStates.personalInfo.email,
      birthDate: cooPersonalStates.personalInfo.birthDate,
      citizenshipStatus: cooPersonalStates.personalInfo.citizenshipStatus,
      ssn_private: cooPersonalStates.personalInfo.ssn_private,
      gender: cooPersonalStates.personalInfo.gender,
      countryOfResidence: cooPersonalStates.personalInfo.countryOfResidence,
    },
  ];

  const oaDecedentInfo = cooPersonalStates.decedentInfo
    ? {
        firstName: cooPersonalStates.decedentInfo.firstName,
        middleName: cooPersonalStates.decedentInfo.middleName,
        lastName: cooPersonalStates.decedentInfo.lastName,
      }
    : {};

  const oaContactInfo = [
    {
      sameAsMailingAddress: cooContactInfo.personalAddress.isStreetAddressSame,
      mailingAddress: cooContactInfo.personalAddress.mailingAddress,
      streetAddress: cooContactInfo.personalAddress.streetAddress,
      primaryPhone: {
        phoneNumber: cooPersonalStates.personalPhone.phoneNumber,
        isMobile: cooPersonalStates.personalPhone.isMobile,
      },
    },
  ];

  const oaEmployment = [
    {
      employmentStatus: employment.employmentStatus.employmentStatus,
      employer: employment.employmentStatus.employer,
      occupation: employment.employmentStatus.occupation,
      address: employment.employmentStatus.address,
      association: employment.associations,
    },
  ];

  const oaGoals = invObj.objectives;

  const oaNetWorth = [
    {
      annualIncome: financial.annualIncome,
      liquidNetWorth: financial.liquidNetWorth,
    },
  ];

  const oaSources = {
    F: true,
  };

  const clientData = {
    selectedAccountTypeId: getOASelectedAccountTypeId(
      transactionTypes.accountType,
      transactionTypes.iraType,
      transactionTypes.iraOption,
    ),
    personalInfo: oaPersonalInfo,
    decedentInfo: oaDecedentInfo,
    contactInfo: oaContactInfo,
    fundingMethod: 'NONE', // TODO: Hardcoded but is it NONE or TRANSFER???
    employmentInfo: oaEmployment,
    goals: oaGoals,
    incomeNetWorth: oaNetWorth,
    sources: oaSources,
    isProspect: isProspect,
    settlementFund: settlementFund ?? SettlementFundAoAEnum.FED,
  };

  switch (clientData.selectedAccountTypeId) {
    case OAAccountTypes.TraditionalIRA:
    case OAAccountTypes.RothIRA:
    case OAAccountTypes.Individual:
      return clientData;
    default:
      return clientData;
  }
}

export function getOASelectedAccountTypeId(
  accountType: AccountTypes,
  iraType?: IRATypes,
  iraOption?: string,
): OAAccountTypes {
  switch (accountType) {
    case AccountTypes.INDV:
    case AccountTypes.JOINT:
    case AccountTypes.UGMA_UTMA:
      return OAAccountTypes.Individual;
    case AccountTypes.IRA:
      if (iraOption === IRAOptions.ASSUME) {
        return iraType === IRATypes.OTHER ? OAAccountTypes.TraditionalIRA : OAAccountTypes.RothIRA;
      } else {
        return iraType === IRATypes.OTHER || iraType === IRATypes.INHERITED_OTHER
          ? OAAccountTypes.InheritedIRA
          : OAAccountTypes.InheritedRothIRA;
      }
    default:
      return OAAccountTypes.UNKNOWN;
  }
}
